/**
 * Tab "Điểm danh HV" — student attendance form scoped to the session from dashboard.
 * Reuses the same reducer/card pattern from $classId.attendance.tsx but bound to a
 * specific sessionId rather than a date-picker flow.
 */
import { useEffect, useMemo, useReducer, useRef } from 'react';
import { toast } from 'sonner';
import { CheckSquare, Save, Lock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useSaveAttendance } from '@client/lib/queries/use-teacher';
import type { DashboardEnrollment, DashboardSession } from '@client/lib/queries/use-session-dashboard';

// ---------------------------------------------------------------------------
// Local types (mirror $classId.attendance.tsx)
// ---------------------------------------------------------------------------
type AttStatus = 'present' | 'absent' | 'late' | 'excused';

interface LocalRecord {
  enrollmentId: string;
  studentName: string;
  studentCode: string;
  status: AttStatus;
  checkInAt: string;
  note: string;
}

type Action =
  | { type: 'init'; records: LocalRecord[] }
  | { type: 'set_all_present' }
  | { type: 'set_status'; enrollmentId: string; status: AttStatus }
  | { type: 'set_note'; enrollmentId: string; note: string }
  | { type: 'set_checkin'; enrollmentId: string; checkInAt: string };

function recordsReducer(state: LocalRecord[], action: Action): LocalRecord[] {
  switch (action.type) {
    case 'init': return action.records;
    case 'set_all_present':
      return state.map((r) => ({ ...r, status: 'present', checkInAt: '', note: '' }));
    case 'set_status':
      return state.map((r) =>
        r.enrollmentId === action.enrollmentId ? { ...r, status: action.status } : r,
      );
    case 'set_note':
      return state.map((r) =>
        r.enrollmentId === action.enrollmentId ? { ...r, note: action.note } : r,
      );
    case 'set_checkin':
      return state.map((r) =>
        r.enrollmentId === action.enrollmentId ? { ...r, checkInAt: action.checkInAt } : r,
      );
    default: return state;
  }
}

// ---------------------------------------------------------------------------
// Status button styles (same palette as $classId.attendance.tsx)
// ---------------------------------------------------------------------------
const STATUS_OPTIONS: { value: AttStatus; label: string; className: string }[] = [
  {
    value: 'present', label: 'Có mặt',
    className: 'border-brand-green-300 bg-brand-green-50 text-brand-green-700 dark:bg-brand-green-950/40 dark:text-brand-green-300 dark:border-brand-green-700 data-[active=true]:bg-brand-green-500 data-[active=true]:text-white data-[active=true]:border-brand-green-500',
  },
  {
    value: 'absent', label: 'Vắng',
    className: 'border-destructive/40 bg-destructive/5 text-destructive dark:border-destructive/60 dark:bg-destructive/10 data-[active=true]:bg-destructive data-[active=true]:text-white data-[active=true]:border-destructive',
  },
  {
    value: 'late', label: 'Trễ',
    className: 'border-brand-orange-300 bg-brand-orange-50 text-brand-orange-700 dark:bg-brand-orange-950/40 dark:text-brand-orange-300 dark:border-brand-orange-700 data-[active=true]:bg-brand-orange-500 data-[active=true]:text-white data-[active=true]:border-brand-orange-500',
  },
  {
    value: 'excused', label: 'Phép',
    className: 'border-[var(--color-info-border)]/40 bg-[var(--color-info)]/5 text-[var(--color-info)] dark:border-[var(--color-info-border)]/50 dark:bg-[var(--color-info-border)]/10 dark:text-[var(--color-info-border)] data-[active=true]:bg-[var(--color-info)] data-[active=true]:text-white data-[active=true]:border-[var(--color-info)]',
  },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface TabAttendanceProps {
  classId: string;
  session: DashboardSession;
  enrollments: DashboardEnrollment[];
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function TabAttendance({ classId, session, enrollments }: TabAttendanceProps) {
  const [records, dispatch] = useReducer(recordsReducer, []);
  const initializedRef = useRef(false);
  const isClosed = session.status === 'closed';

  // Initialise from dashboard enrollments (run once)
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const local: LocalRecord[] = enrollments.map((e) => ({
      enrollmentId: e.enrollmentId,
      studentName: e.studentName,
      studentCode: e.studentCode ?? '',
      status: (e.attendanceStatus as AttStatus) ?? 'present',
      checkInAt: '',
      note: '',
    }));
    dispatch({ type: 'init', records: local });
  }, [enrollments]);

  const saveAttendance = useSaveAttendance(classId, session.id);

  const summary = useMemo(() => {
    const counts = { present: 0, absent: 0, late: 0, excused: 0 };
    records.forEach((r) => { counts[r.status] = (counts[r.status] ?? 0) + 1; });
    return counts;
  }, [records]);

  async function handleSave() {
    try {
      await saveAttendance.mutateAsync({
        records: records.map((r) => ({
          enrollmentId: r.enrollmentId,
          status: r.status,
          checkInAt: r.checkInAt || undefined,
          note: r.note || undefined,
        })),
      });
      toast.success('Đã lưu điểm danh.');
    } catch {
      toast.error('Lỗi khi lưu điểm danh.');
    }
  }

  if (enrollments.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Lớp chưa có học viên ghi danh.
      </p>
    );
  }

  return (
    <div className="space-y-4 pb-24 md:pb-4">
      {/* Closed banner */}
      {isClosed && (
        <Alert className="border-brand-orange-200 bg-brand-orange-50 dark:border-brand-orange-700/50 dark:bg-brand-orange-950/30">
          <Lock className="h-4 w-4 text-brand-orange-600 dark:text-brand-orange-400" />
          <AlertDescription className="text-brand-orange-700 dark:text-brand-orange-300 text-sm">
            Buổi đã chốt — cần Admin mở lại để chỉnh sửa điểm danh.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary bar */}
      <div className="flex flex-wrap gap-3 text-xs font-medium rounded-lg bg-muted/50 px-4 py-2.5">
        <span className="text-brand-green-600 dark:text-brand-green-400">✓ Có mặt: {summary.present}</span>
        <span className="text-destructive">✗ Vắng: {summary.absent}</span>
        <span className="text-brand-orange-600 dark:text-brand-orange-400">⏱ Trễ: {summary.late}</span>
        <span className="text-[var(--color-info)] dark:text-[var(--color-info-border)]">📋 Có phép: {summary.excused}</span>
        <span className="text-muted-foreground">/ {records.length} HV</span>
      </div>

      {/* Quick all-present */}
      {!isClosed && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => dispatch({ type: 'set_all_present' })}
          className="min-h-[44px] w-full sm:w-auto"
        >
          <CheckSquare className="mr-2 h-4 w-4" />
          Tất cả có mặt
        </Button>
      )}

      {/* Student cards */}
      <div className="flex flex-col gap-3">
        {records.map((r) => (
          <AttendanceCard
            key={r.enrollmentId}
            record={r}
            disabled={isClosed}
            dispatch={dispatch}
          />
        ))}
      </div>

      {/* Action bar */}
      {!isClosed && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-background border-t border-border p-3 flex gap-2 md:static md:border-0 md:p-0 md:bg-transparent md:mt-4">
          <Button
            className="flex-1 min-h-[44px]"
            onClick={() => void handleSave()}
            disabled={saveAttendance.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            {saveAttendance.isPending ? 'Đang lưu...' : 'Lưu điểm danh'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual attendance card (same as $classId.attendance.tsx)
// ---------------------------------------------------------------------------
function AttendanceCard({
  record,
  disabled,
  dispatch,
}: {
  record: LocalRecord;
  disabled: boolean;
  dispatch: React.Dispatch<Action>;
}) {
  return (
    <div className={`rounded-lg border border-border bg-card p-3 space-y-3 ${disabled ? 'opacity-70' : ''}`}>
      <div>
        <p className="text-sm font-medium text-foreground">{record.studentName}</p>
        {record.studentCode && (
          <p className="text-xs font-mono text-muted-foreground">{record.studentCode}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            data-active={record.status === opt.value}
            disabled={disabled}
            onClick={() =>
              dispatch({ type: 'set_status', enrollmentId: record.enrollmentId, status: opt.value })
            }
            className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors min-h-[36px] disabled:cursor-not-allowed ${opt.className}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {record.status === 'late' && !disabled && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground shrink-0">Giờ vào:</label>
          <Input
            type="time"
            value={record.checkInAt}
            onChange={(e) =>
              dispatch({ type: 'set_checkin', enrollmentId: record.enrollmentId, checkInAt: e.target.value })
            }
            className="h-8 w-28 text-xs"
          />
        </div>
      )}

      {(record.status !== 'present' || record.note) && !disabled && (
        <Textarea
          value={record.note}
          onChange={(e) =>
            dispatch({ type: 'set_note', enrollmentId: record.enrollmentId, note: e.target.value })
          }
          placeholder="Ghi chú (lý do vắng/trễ...)"
          className="text-xs h-16 resize-none"
          maxLength={200}
        />
      )}

      {disabled && record.note && (
        <p className="text-xs text-muted-foreground italic">{record.note}</p>
      )}
    </div>
  );
}
